import React, { ErrorInfo } from 'react'
import { captureException, withScope } from '@sentry/browser'
import { Oops } from '../Oops'
import { Body1 } from '../Typography'
import { appHistory } from '../../utils/history'
import { ButtonLightBorder } from '../Buttons'
import { spacing } from '../../theme'
import { isLogoutError } from '../../utils/Sentry'

type Props = { children: React.JSX.Element }
export class ErrorBoundary extends React.Component<Props, { error?: Error }> {
  constructor(props: Props) {
    super(props)
    this.state = {
      error: undefined,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    withScope((scope) => {
      // Log operation name and path as extras
      scope.setExtra('componentStack', errorInfo.componentStack)
      scope.setTag('is_boundary_error', 'true')
      const level = isLogoutError(error) ? 'warning' : 'error'
      captureException(error, { level })
      // If the error has already been captured sentry will not do a second capture
      // but we really want to have a capture here to set an alert based on the tag
      // so we capture a new error which will not have been captured anywhere else
      captureException(
        new Error(`Boundary error with message: ${error.message}`),
        { level },
      )
    })

    this.setState({ error })
  }

  render(): React.JSX.Element | undefined {
    const { error } = this.state
    const { children } = this.props

    // When we fail to refresh the token in the errorLink we thorw a logoutError.
    // This error get encapsullated in a GraphqlError in an ApolloError.
    // If we get this error we should log the user out instead of displaying an error message.
    if (isLogoutError(error)) {
      appHistory.push('/logout')
      return undefined
    }

    if (error) {
      return (
        <Oops errorMessage={error.message}>
          <ButtonLightBorder
            style={{
              marginTop: spacing.large,
            }}
            onClick={() => this.setState({ error: undefined })}
          >
            <Body1>Ok</Body1>
          </ButtonLightBorder>
        </Oops>
      )
    }

    return children
  }
}
